import React, { useState, useRef, useEffect } from "react";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import Spinner from "shared/components/common/spinner";

interface FilterDropdownProps {
  label?: string;
  icon?: React.ReactNode;
  selected?: string;
  options: string[] | React.ReactNode[];
  onSelect?: (value: string) => void;
  menuWidth?: number | string;
  menuPosition?: "left" | "right";
  hideLabelOnSelected?: boolean;
  isVerticalTrigger?: boolean;
  preventCloseOnSelect?: (value: string) => boolean;
  maxHeight?: string;
  onScrollEnd?: () => void;
  loading?: boolean;
}

const FilterDropdown = ({
  label,
  icon,
  selected: selectedProp,
  options,
  onSelect,
  menuWidth = "140px",
  menuPosition = "left",
  hideLabelOnSelected = false,
  isVerticalTrigger = false,
  preventCloseOnSelect,
  maxHeight = "auto",
  onScrollEnd,
  loading = false,
}: FilterDropdownProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const firstStringOption = options.find((opt) => typeof opt === "string") as
    | string
    | undefined;
  const initialValue = useRef<string>(selectedProp || firstStringOption || "");
  const [selected, setSelected] = useState<string>(initialValue.current);
  const ref = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuCoordinates, setMenuCoordinates] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const onScrollEndRef = useRef<(() => void) | undefined>(onScrollEnd);

  useEffect(() => {
    if (selectedProp !== undefined) {
      setSelected(selectedProp);
    }
  }, [selectedProp]);

  useEffect(() => {
    onScrollEndRef.current = onScrollEnd;
  }, [onScrollEnd]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      isScrollingRef.current = false;
      return;
    }

    const handleScroll = () => {
      const menu = menuRef.current;
      if (!menu || isScrollingRef.current) return;

      // Check if onScrollEnd is available using ref
      const currentOnScrollEnd = onScrollEndRef.current;
      if (!currentOnScrollEnd) {
        return;
      }

      const scrollTop = menu.scrollTop;
      const scrollHeight = menu.scrollHeight;
      const clientHeight = menu.clientHeight;

      // Check if element is actually scrollable
      if (scrollHeight <= clientHeight) {
        return;
      }

      // Calculate distance from bottom - more lenient threshold
      const threshold = 50;
      const scrollBottom = scrollTop + clientHeight;
      const distanceFromBottom = scrollHeight - scrollBottom;
      const isAtBottom = distanceFromBottom <= threshold;

      if (isAtBottom) {
        isScrollingRef.current = true;
        try {
          currentOnScrollEnd();
        } catch (error) {
          console.error("Error in onScrollEnd:", error);
        }
        // Reset after a delay to allow for loading state
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 2000);
      }
    };

    // Attach listener with multiple attempts to ensure it's attached
    let timeoutId: NodeJS.Timeout;
    let rafId: number;
    let menuEl: HTMLElement | null = null;

    const attachListener = () => {
      const menu = menuRef.current;
      if (menu && open) {
        menuEl = menu;
        menu.removeEventListener("scroll", handleScroll);
        menu.addEventListener("scroll", handleScroll, { passive: true });
      }
    };

    rafId = requestAnimationFrame(() => {
      attachListener();
      timeoutId = setTimeout(attachListener, 200);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (menuEl) {
        menuEl.removeEventListener("scroll", handleScroll);
      }
      isScrollingRef.current = false;
    };
  }, [open, options.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updateMenuPosition = () => {
      if (open && ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const position: { top: number; left?: number; right?: number } = {
          top: rect.bottom + 4,
        };

        if (menuPosition === "right") {
          position.right = window.innerWidth - rect.right;
        } else {
          position.left = rect.left;
        }

        setMenuCoordinates(position);
      } else {
        setMenuCoordinates(null);
      }
    };

    updateMenuPosition();

    if (open) {
      window.addEventListener("scroll", updateMenuPosition, true);
      window.addEventListener("resize", updateMenuPosition);
      return () => {
        window.removeEventListener("scroll", updateMenuPosition, true);
        window.removeEventListener("resize", updateMenuPosition);
      };
    }
  }, [open, menuPosition]);

  return (
    // <div className="flex gap-4 items-center w-full">
    <div className={styles.dropdown} ref={ref}>
      {isVerticalTrigger ? (
        <span
          className={classNames(styles.iconDropdownToggle, "cursor-pointer")}
          onClick={() => setOpen(!open)}
        >
          <Icons.VerticalDots />
        </span>
      ) : (
        <div
          className={classNames(
            styles.dropdownToggle,
            "flex items-center gap-1 cursor-pointer",
          )}
          onClick={() => setOpen(!open)}
        >
          <span className={styles.icon}>{icon}</span>
          <label
            className={classNames(styles.label, {
              [styles.hidden]:
                hideLabelOnSelected && selected !== initialValue.current,
            })}
          >
            {label}
            {!!label && ":"}
          </label>
          <span className={styles.selectedValue}>{selected}</span>
          <Icons.ChevDown
            className={classNames(styles.chevron, { [styles.expanded]: open })}
          />
        </div>
      )}

      {open && menuCoordinates && (
        <div
          ref={menuRef}
          className={classNames(styles.dropdownMenu, styles.hideScrollbar)}
          style={{
            // min-width beats max-width, so fit-content must go on width or the cap is ignored.
            ...(menuWidth === "fit-content"
              ? { width: "fit-content" as const }
              : { minWidth: menuWidth }),
            maxWidth: "min(90vw, 420px)",
            position: "fixed",
            top: `${menuCoordinates.top}px`,
            left:
              menuCoordinates.left !== undefined
                ? `${menuCoordinates.left}px`
                : "auto",
            right:
              menuCoordinates.right !== undefined
                ? `${menuCoordinates.right}px`
                : "auto",
            maxHeight: maxHeight,
            overflowY: maxHeight !== "auto" ? "auto" : "visible",
          }}
        >
          {options
            .filter((option) => typeof option === "string")
            .map((option) => {
              const optionString = option as string;
              const shouldPreventClose = preventCloseOnSelect?.(optionString);
              return (
                <div
                  key={optionString}
                  className={classNames(styles.dropdownOption, {
                    [styles.active]: optionString === selected,
                  })}
                  onClick={() => {
                    if (!shouldPreventClose) {
                      setSelected(optionString);
                    }
                    onSelect?.(optionString);
                    if (!shouldPreventClose) {
                      setOpen(false);
                    }
                  }}
                >
                  {isVerticalTrigger && <Icons.ChatOutline />}
                  {optionString}
                </div>
              );
            })}
          {loading && (
            <div className="flex items-center justify-center py-3 px-2">
              <Spinner />
            </div>
          )}
        </div>
      )}
    </div>
    // </div>
  );
};

export default FilterDropdown;
